<template>
  <div class="accounting-target-page">
    <v-app-bar color="#ff9800" dark dense>
      <v-btn icon @click="goBack"><v-icon>mdi-arrow-left</v-icon></v-btn>
      <v-toolbar-title class="font-weight-bold">
        <v-icon class="mr-2">mdi-calculator-variant</v-icon>
        Edit Adjustment Target (Accounting)
      </v-toolbar-title>
      <v-spacer></v-spacer>
    </v-app-bar>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="4000" top shaped>
      {{ snackbar.text }}
    </v-snackbar>

    <v-container fluid class="pa-6">
      <div v-if="checkingAccess" class="text-center pa-10">
        <v-progress-circular indeterminate color="orange" size="56"></v-progress-circular>
        <div class="mt-4 text-subtitle-1 grey--text">Memeriksa status posisi…</div>
      </div>

      <div v-else-if="!accessAllowed" class="text-center pa-10">
        <v-icon size="64" color="red lighten-1">mdi-lock-alert-outline</v-icon>
        <div class="mt-3 text-subtitle-1 red--text text--darken-2">
          Posisi ini berstatus "{{ statusLabel }}". Adjustment target hanya bisa diakses saat status "Adjust Accounting".
        </div>
        <v-btn class="mt-4" outlined color="primary" @click="goBack">Kembali</v-btn>
      </div>

      <div v-else-if="loading" class="text-center pa-10">
        <v-progress-circular indeterminate color="orange" size="56"></v-progress-circular>
        <div class="mt-4 text-subtitle-1 grey--text">Memuat data accounting…</div>
      </div>

      <div v-else-if="!items.length" class="text-center pa-10 grey--text">
        <v-icon size="64" color="grey lighten-1">mdi-information-outline</v-icon>
        <div class="mt-3 text-subtitle-1">Tidak ada KPI accounting (is_accounting) pada posisi ini.</div>
      </div>

      <template v-else>
        <v-card v-for="item in items" :key="item.id" class="mb-6 elevation-2 rounded-lg">
          <v-card-title class="py-3 px-4 white--text" style="background:#ef6c00;">
            <v-icon left dark>mdi-target</v-icon>
            <span class="text-subtitle-1 font-weight-bold">{{ item.kpi_name }}</span>
            <v-chip v-if="item.manual" x-small label color="deep-purple accent-2" class="ml-2 white--text font-weight-bold">
              <v-icon x-small left>mdi-pencil-box-outline</v-icon> Manual Adjustment
            </v-chip>
            <v-chip v-if="!item.manual" x-small label color="blue-grey lighten-1" class="ml-2 white--text font-weight-bold">
              <v-icon x-small left>mdi-sigma</v-icon> Kalkulasi Vertikal: {{ item.vertical_id === 2 ? 'AVG' : 'SUM' }}
            </v-chip>
            <v-spacer></v-spacer>
            <v-chip small label color="orange lighten-4" class="mr-2">
              <span class="orange--text text--darken-4">Annual Target: {{ formatThousandId(item.annual_target || 0) }}</span>
            </v-chip>
            <v-chip small label color="deep-purple lighten-4">
              <span class="deep-purple--text text--darken-4">Annual Adjustment: {{ formatThousandId(item.annual_adjustment || 0) }}</span>
            </v-chip>
          </v-card-title>
          <v-card-text class="pa-0">
            <v-simple-table class="accounting-table">
              <thead>
                <tr>
                  <th class="text-left" style="width:200px;">Owner</th>
                  <th class="text-left" style="width:200px;">Jabatan</th>
                  <th class="text-left">Bulan</th>
                  <th class="text-right" style="width:170px;">Target</th>
                  <th class="text-right" style="width:170px;">Actual</th>
                  <th class="text-right" style="width:190px;">Adjustment Target</th>
                </tr>
              </thead>
              <!-- Tanpa child: owner/jabatan rowspan -->
              <tbody v-if="!item.hasChildren">
                <tr v-for="(m, idx) in item.months" :key="m.periode">
                  <td v-if="idx === 0" :rowspan="item.months.length" class="font-weight-medium align-top">{{ item.owner_name }}</td>
                  <td v-if="idx === 0" :rowspan="item.months.length" class="align-top">{{ item.owner_position }}</td>
                  <td class="font-weight-medium">{{ m.label }}</td>
                  <td class="text-right grey--text text--darken-1">{{ formatThousandId(m.target || 0) }}</td>
                  <td class="text-right grey--text text--darken-1">{{ formatThousandId(m.actual || 0) }}</td>
                  <td>
                    <v-text-field
                      :value="formatThousandId(m.adjustment || 0)"
                      @input="val => { m.adjustment = toNum(val) }"
                      :readonly="!m.monthEditable"
                      :disabled="!m.monthEditable"
                      dense outlined hide-details single-line reverse
                      class="adj-input"
                      :class="{ 'adj-input--editable': m.monthEditable }"
                    />
                  </td>
                </tr>
              </tbody>
              <!-- Dengan child: parent + child/grandchild digabung per bulan, menjorok. Leaf editable, parent dihitung -->
              <tbody v-else>
                <template v-for="m in item.months">
                  <tr
                    v-for="(node, ni) in item.flat"
                    :key="m.periode + '-' + node.id"
                    :class="ni === 0 ? 'parent-row' : 'child-row'"
                  >
                    <td :style="{ paddingLeft: (12 + node.level * 22) + 'px' }" :class="node.level === 0 ? 'font-weight-bold' : ''">
                      <v-icon v-if="node.level > 0" x-small color="grey" class="mr-1">mdi-subdirectory-arrow-right</v-icon>{{ node.owner_name }}
                    </td>
                    <td :class="node.level === 0 ? 'font-weight-bold' : 'grey--text text--darken-1'">{{ node.owner_position }}</td>
                    <td v-if="ni === 0" :rowspan="item.flat.length" class="font-weight-medium align-top month-cell">{{ m.label }}</td>
                    <td class="text-right grey--text text--darken-1">{{ formatThousandId(cellTarget(node, m.periode)) }}</td>
                    <td class="text-right grey--text text--darken-1">{{ formatThousandId(cellActual(node, m.periode)) }}</td>
                    <td>
                      <v-text-field
                        v-if="node.editable"
                        :value="formatThousandId((node.monthsByPeriode[m.periode] || {}).adjustment || 0)"
                        @input="val => { if (node.monthsByPeriode[m.periode]) node.monthsByPeriode[m.periode].adjustment = toNum(val) }"
                        :readonly="!(node.monthsByPeriode[m.periode] || {}).monthEditable"
                        :disabled="!(node.monthsByPeriode[m.periode] || {}).monthEditable"
                        dense outlined hide-details single-line reverse
                        class="adj-input"
                        :class="{ 'adj-input--editable': (node.monthsByPeriode[m.periode] || {}).monthEditable }"
                      />
                      <span v-else class="d-block text-right grey--text font-weight-medium pr-2">{{ formatThousandId(adjFor(node, m.periode)) }}</span>
                    </td>
                  </tr>
                </template>
              </tbody>
              <tfoot>
                <tr class="total-row">
                  <td colspan="3" class="font-weight-bold">Total Tahunan</td>
                  <td class="text-right font-weight-bold">{{ formatThousandId(item.annual_target) }}</td>
                  <td class="text-right"></td>
                  <td>
                    <v-text-field
                      v-if="item.manual"
                      :value="formatThousandId(item.manualTotal || 0)"
                      @input="val => { item.manualTotal = toNum(val) }"
                      dense outlined hide-details single-line reverse
                      class="adj-input adj-input--total adj-input--editable"
                    />
                    <span v-else class="d-block text-right font-weight-bold pr-2">{{ formatThousandId(itemTotalAdjustment(item)) }}</span>
                  </td>
                </tr>
              </tfoot>
            </v-simple-table>
            <div class="d-flex align-center px-4 py-3" style="gap:8px;">
              <v-spacer></v-spacer>
              <v-btn x-small outlined color="primary" class="text-none" @click="copyFromTarget(item)">
                <v-icon left x-small>mdi-content-copy</v-icon> Copy dari Target
              </v-btn>
              <v-btn x-small text color="grey darken-1" class="text-none" @click="resetItem(item)">
                <v-icon left x-small>mdi-refresh</v-icon> Reset
              </v-btn>
            </div>
          </v-card-text>
        </v-card>

        <div class="d-flex justify-end mt-2" style="gap:8px;">
          <v-btn
            color="#ef6c00"
            dark
            large
            :loading="saving"
            :disabled="publishing"
            @click="save"
          >
            <v-icon left>mdi-content-save</v-icon> Simpan Adjustment
          </v-btn>
          <v-btn
            color="green darken-1"
            dark
            large
            :loading="publishing"
            :disabled="saving"
            @click="saveAndPublish"
          >
            <v-icon left>mdi-check-decagram</v-icon> Simpan dan Publish
          </v-btn>
        </div>
      </template>
    </v-container>
  </div>
</template>

<script>
export default {
  name: "AccountingTargetPage",
  props: {
    chartId: { type: [String, Number], required: true },
  },
  data() {
    return {
      checkingAccess: true,
      accessAllowed: false,
      statusLabel: "",
      loading: false,
      saving: false,
      publishing: false,
      items: [],
      snackbar: { show: false, color: "success", text: "" },
    };
  },
  mounted() {
    this.checkAccess();
  },
  methods: {
    formatThousandId(v) {
      const n = this.toNum(v);
      // Format standar: koma pemisah ribuan, titik desimal (mis. 1,000,000.12).
      return n.toLocaleString("en-US", { maximumFractionDigits: 2 });
    },
    toNum(v) {
      // Angka JS asli (sudah di-parse sebelumnya) — pakai langsung, jangan bolak-balik lewat string.
      if (typeof v === "number") return isNaN(v) ? 0 : v;
      const s = String(v ?? "").trim();
      if (s === "" || s === "-") return 0;
      // Format standar (koma ribuan, titik desimal) cocok baik untuk ketikan user
      // maupun angka mentah decimal(15,2) dari API — cukup buang koma lalu parseFloat.
      const n = parseFloat(s.replace(/,/g, ""));
      return isNaN(n) ? 0 : n;
    },
    goBack() {
      this.$router.push("/setup-list");
    },
    async checkAccess() {
      this.checkingAccess = true;
      try {
        const res = await this.$axios.get(
          `${process.env.VUE_APP_URL}/api/charts/detail/${this.chartId}`,
          { headers: { Authorization: `Bearer ${this.$store.getters.isTokenUser}` } }
        );
        const status = Number(res.data.status_publish);
        this.statusLabel = status === 1 ? "Published" : status === 2 ? "Adjust Accounting" : "Unpublished";
        this.accessAllowed = status === 2;
      } catch (err) {
        this.accessAllowed = false;
        this.statusLabel = "tidak diketahui";
      } finally {
        this.checkingAccess = false;
      }
      if (this.accessAllowed) this.load();
    },
    async load() {
      this.loading = true;
      try {
        const res = await this.$axios.get(
          `${process.env.VUE_APP_URL}/api/kpi-item/${this.chartId}`,
          { headers: { Authorization: `Bearer ${this.$store.getters.isTokenUser}` } }
        );
        const raw = Array.isArray(res.data) ? res.data : res.data.kpi_item || [];
        this.items = raw
          // hanya item milik chart ini; item shared (milik chart lain) diatur di owner-nya
          .filter((it) => Number(it.is_accounting) === 1 && it.status_kpi !== "shared")
          .map((it) => this.buildItem(it));
      } catch (err) {
        this.snackbar = { show: true, color: "error", text: "Gagal memuat data accounting." };
      } finally {
        this.loading = false;
      }
    },
    buildItem(it) {
      const top = this.buildNode(it, 0);
      top.flat = this.flatten(top); // urutan DFS untuk render baris
      return top;
    },
    buildNode(it, level) {
      const manual = Boolean(Number(it.manual_adjustment));
      const hasChildren = Array.isArray(it.children) && it.children.length > 0;
      // leaf (terbawah) atau manual → editable; parent dihitung vertical dari child
      const editable = !hasChildren || manual;
      const months = (Array.isArray(it.values) ? it.values : [])
        .slice()
        .sort((a, b) => String(a.periode).localeCompare(String(b.periode)))
        .map((v) => {
          const moNum = parseInt(String(v.periode).split(/[-/]/)[1] || "0", 10);
          const monthEditable = moNum >= 7 && editable; // Jul-Des editable, Jan-Jun = actual
          const actual = this.toNum(v.actual);
          const saved = v.target_adjustment;
          return {
            periode: v.periode,
            label: this.monthLabel(v.periode),
            moNum,
            target: this.toNum(v.target),
            actual,
            monthEditable,
            adjustment: moNum >= 7
              ? (saved !== null && saved !== undefined ? this.toNum(saved) : 0)
              : actual,
          };
        });
      const monthsByPeriode = {};
      months.forEach((m) => { monthsByPeriode[m.periode] = m; });
      return {
        id: it.id,
        kpi_name: it.kpi_name,
        annual_target: this.toNum(it.annual_target),
        annual_adjustment: this.toNum(it.annual_adjustment),
        owner_name: (it.owner && it.owner.user && it.owner.user.name) || it.owner_name || "-",
        owner_position: (it.owner && it.owner.position_name) || it.owner_position_name || "-",
        level,
        manual,
        hasChildren,
        editable,
        vertical_id: Number(it.vertical_id) || 1, // 1=SUM, 2=AVERAGE
        kalkulasi_id: Number(it.kalkulasi_id) || 1, // 1=Sum, 2=Avg, 3=Max, 4=Last
        manualTotal: manual ? this.toNum(it.target_adjustment_total) : 0,
        months,
        monthsByPeriode,
        children: hasChildren ? it.children.map((c) => this.buildNode(c, level + 1)) : [],
      };
    },
    flatten(node) {
      let out = [node];
      (node.children || []).forEach((c) => { out = out.concat(this.flatten(c)); });
      return out;
    },
    monthLabel(periode) {
      const names = ["Jan","Feb","Mar","Apr","Mei","Jun","Jul","Agu","Sep","Okt","Nov","Des"];
      const parts = String(periode).split(/[-/]/);
      const mo = parseInt(parts[1] || "0", 10);
      return mo >= 1 && mo <= 12 ? `${names[mo - 1]} ${parts[0]}` : periode;
    },
    cellTarget(node, periode) { const m = node.monthsByPeriode[periode]; return m ? m.target : 0; },
    cellActual(node, periode) { const m = node.monthsByPeriode[periode]; return m ? m.actual : 0; },
    // Adjustment 1 node utk 1 bulan — rekursif bottom-up sesuai vertical_id
    adjFor(node, periode) {
      if (node.editable) {
        const m = node.monthsByPeriode[periode];
        return m ? this.toNum(m.adjustment) : 0;
      }
      const vals = (node.children || []).map((c) => this.adjFor(c, periode));
      if (!vals.length) return 0;
      const sum = vals.reduce((a, b) => a + b, 0);
      return node.vertical_id === 2 ? Math.round(sum / vals.length) : sum;
    },
    // Gabungkan nilai 12 bulan sesuai aturan kalkulasi item (Sum/Avg/Max/Last) — bukan selalu dijumlah.
    aggregateByKalkulasi(kalkulasiId, values) {
      if (!values.length) return 0;
      switch (Number(kalkulasiId)) {
        case 2: return values.reduce((a, b) => a + b, 0) / values.length; // AVG
        case 3: return Math.max(...values); // MAX
        case 4: return values[values.length - 1]; // LAST (bulan sudah urut asc → Des)
        default: return values.reduce((a, b) => a + b, 0); // SUM
      }
    },
    itemTotalAdjustment(node) {
      if (node.manual) return this.toNum(node.manualTotal);
      const vals = node.months.map((m) => this.adjFor(node, m.periode));
      return this.aggregateByKalkulasi(node.kalkulasi_id, vals);
    },
    copyFromTarget(top) {
      this.flatten(top).forEach((n) => {
        if (!n.editable) return;
        if (n.manual) n.manualTotal = n.annual_target;
        n.months.forEach((m) => { if (m.monthEditable) m.adjustment = this.toNum(m.target); });
      });
    },
    resetItem(top) {
      this.flatten(top).forEach((n) => {
        if (!n.editable) return;
        if (n.manual) n.manualTotal = 0;
        n.months.forEach((m) => { if (m.monthEditable) m.adjustment = 0; });
      });
    },
    async persistAdjustments() {
      const values = [];
      const itemTotals = [];
      const annualAdjustments = [];
      this.items.forEach((top) => {
        this.flatten(top).forEach((n) => {
          if (!n.editable) return; // parent dihitung otomatis, tidak disimpan langsung
          n.months.forEach((m) => {
            values.push({ kpi_item_id: n.id, periode: m.periode, target_adjustment: this.toNum(m.adjustment) });
          });
          if (n.manual) {
            itemTotals.push({ kpi_item_id: n.id, target_adjustment_total: this.toNum(n.manualTotal) });
          }
        });
        // Total Tahunan efektif (manual atau rollup children) → disimpan ke item top-level.
        const annualAdjustment = this.itemTotalAdjustment(top);
        annualAdjustments.push({ kpi_item_id: top.id, annual_adjustment: annualAdjustment });
        top.annual_adjustment = annualAdjustment; // reflect langsung di header tanpa perlu reload
      });
      await this.$axios.post(
        `${process.env.VUE_APP_URL}/api/kpi-value/adjustment-batch`,
        { values, item_totals: itemTotals, annual_adjustments: annualAdjustments },
        { headers: { Authorization: `Bearer ${this.$store.getters.isTokenUser}` } }
      );
    },
    async save() {
      this.saving = true;
      try {
        await this.persistAdjustments();
        this.snackbar = { show: true, color: "success", text: "Adjustment target tersimpan." };
      } catch (err) {
        this.snackbar = { show: true, color: "error", text: "Gagal menyimpan adjustment target." };
      } finally {
        this.saving = false;
      }
    },
    async saveAndPublish() {
      this.publishing = true;
      try {
        await this.persistAdjustments();
        await this.$axios.post(
          `${process.env.VUE_APP_URL}/api/charts/status`,
          { status: 1, chart_id: this.chartId },
          { headers: { Authorization: `Bearer ${this.$store.getters.isTokenUser}` } }
        );
        this.$router.push("/setup-list");
      } catch (err) {
        this.snackbar = { show: true, color: "error", text: "Gagal menyimpan & publish." };
        this.publishing = false;
      }
    },
  },
};
</script>

<style scoped>
.accounting-table th {
  background: #fff3e0;
  font-weight: 600;
  color: #e65100 !important;
}
.adj-input >>> .v-input__slot { min-height: 34px !important; }
.adj-input--editable >>> .v-input__slot { background: #fffaf0; }
.adj-input--total >>> input { font-weight: 700; }
.adj-input--match >>> .v-input__slot { background: #e6f4ea !important; }
.adj-input--match >>> input { color: #1b7a3a !important; }
.adj-input--mismatch >>> .v-input__slot { background: #fdecea !important; }
.adj-input--mismatch >>> input { color: #c0392b !important; }
.total-row td { border-top: 2px solid #ef6c00; }
.parent-row > td { background: #fff8f0; }
.parent-row .month-cell { background: #fff; vertical-align: middle; text-align: center; }
.child-row > td { font-size: .8rem; background: #fcfcfd; }
.child-row > td:first-child { white-space: nowrap; }
</style>
